// Google Analytics utility for tracking events
// Add your GA4 Measurement ID to .env as REACT_APP_GA_MEASUREMENT_ID

import { getCookieConsent } from '../components/CookieConsent';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Initialize GA (only if consent given)
export const initGA = () => {
  if (!GA_MEASUREMENT_ID) {
    console.log('GA Measurement ID not configured');
    return;
  }

  if (!getCookieConsent()) {
    console.log('GA not initialized - no cookie consent');
    return;
  }

  // Add gtag script
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.async = true;
  document.head.appendChild(script);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID);
  
  console.log('GA initialized with consent');
};

// Set user properties (like cohort) for filtering in GA
export const setUserProperties = (properties) => {
  if (!getCookieConsent() || !window.gtag) {
    return;
  }
  
  window.gtag('set', 'user_properties', properties);
  console.log('GA User Properties set:', properties);
};

// Set user cohort (alpha/beta/production)
export const setUserCohort = (cohort) => {
  if (cohort) {
    setUserProperties({ user_cohort: cohort });
  }
};

// Track custom events (only if consent given)
export const trackEvent = (eventName, parameters = {}) => {
  if (!getCookieConsent()) {
    console.log('GA Event (blocked - no consent):', eventName, parameters);
    return;
  }
  
  if (!window.gtag) {
    console.log('GA Event (not sent - gtag not loaded):', eventName, parameters);
    return;
  }
  
  window.gtag('event', eventName, parameters);
  console.log('GA Event:', eventName, parameters);
};

// Specific event trackers
export const trackSignup = (commitmentHours) => {
  trackEvent('signup', {
    commitment_hours: commitmentHours,
  });
};

export const trackWeekStarted = (weekNumber) => {
  trackEvent('week_started', {
    week_number: weekNumber,
  });
};

export const trackActivityStarted = (weekNumber, activityTitle) => {
  trackEvent('activity_started', {
    week_number: weekNumber,
    activity_title: activityTitle,
  });
};

export const trackActivityCompleted = (weekNumber, activityTitle, timeSpentSeconds) => {
  trackEvent('activity_completed', {
    week_number: weekNumber,
    activity_title: activityTitle,
    time_spent_seconds: timeSpentSeconds,
    time_spent_minutes: Math.round(timeSpentSeconds / 60),
  });
};

export const trackAIInsightGenerated = (weekNumber) => {
  trackEvent('ai_insight_generated', {
    week_number: weekNumber,
  });
};

export const trackAIInsightFeedback = (weekNumber, activityTitle, isHelpful) => {
  trackEvent('ai_insight_feedback', {
    week_number: weekNumber,
    activity_title: activityTitle,
    is_helpful: isHelpful,
    feedback: isHelpful ? 'positive' : 'negative',
  });
};

export const trackHomeworkChecked = (weekNumber, taskNumber, isChecked) => {
  trackEvent('homework_checked', {
    week_number: weekNumber,
    task_number: taskNumber,
    is_checked: isChecked,
  });
};

export const trackCourseCompleted = (totalActivities, expectedActivities, completionType) => {
  trackEvent('course_completed', {
    total_activities: totalActivities,
    expected_activities: expectedActivities,
    completion_type: completionType, // 'full' or 'partial'
    completion_rate: Math.round((totalActivities / expectedActivities) * 100),
  });
};

export const trackPDFDownload = (type) => {
  trackEvent('pdf_downloaded', {
    type: type, // 'certificate' or 'full_programme'
  });
};

export const trackPageView = (pageName) => {
  trackEvent('page_view', {
    page_name: pageName,
  });
};
